/**
 * AssistantMessage.tsx
 *
 * Renders the full assistant message bubble including all 15 visual states
 * (see §3 of ui_redesign_phase1b.md). Owns `activeSource` state for the
 * source document modal and `copyText` state for the clipboard button.
 *
 * Small presentational helpers (PiaAvatar, RoutingBadge, DocumentRetrievalCard,
 * ChartLoadingPlaceholder, etc.) live in AssistantMessage.parts.tsx.
 *
 * Off-limits: ConfirmationCard, ChartRenderer, SourceDocumentModal,
 * PersonPill, ContactPill, CopyMessageButton — no internal changes to these.
 */

import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage as ChatMessageType, AuthUser } from "../../types/index.js";
import ChartRenderer from "../charts/ChartRenderer.js";
import ConfirmationCard from "../ConfirmationCard.js";
import SourceDocumentModal from "../SourceDocumentModal.js";
import PersonPill from "../PersonPill.js";
import ContactPill from "../ContactPill.js";
import CopyMessageButton from "../CopyMessageButton.js";
import {
	PiaAvatar,
	RoutingBadge,
	DocumentRetrievalCard,
	ChartLoadingPlaceholder,
	reactNodeToString,
	applyHighlightsToChildren,
	formatTimestamp,
	formatTimeOnly,
} from "./AssistantMessage.parts.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AssistantMessageProps {
	message: ChatMessageType;
	/** True only for the last assistant message while the stream is active. */
	isStreaming?: boolean;
	/** The authenticated user — not used by assistant messages directly but accepted for prop-shape compatibility. */
	user?: AuthUser;
	/** Renders the error variant when true. Callers set this for `message.isError` routing. */
	variant?: "error";
	/** Called when the user approves an action proposal. */
	onApproveAction?: (actionId: string) => void;
	/** Called when the user rejects an action proposal. */
	onRejectAction?: (actionId: string) => void;
}

interface ActiveSourceState {
	documentId: string;
	fileName: string;
	workdriveFileId?: string;
}

// ---------------------------------------------------------------------------
// AssistantMessage
// ---------------------------------------------------------------------------

/**
 * Renders a single assistant message bubble.
 *
 * Visual states:
 * - State A: Thinking (no content, no classification) — avatar + bouncing dots
 * - State B: Classified, not yet streaming — avatar + dots + routing badge
 * - State C: Streaming — avatar, header, markdown text with blinking cursor
 * - State D: Complete — avatar, header, markdown text, optional source pills
 * - State E: Error — red error block (activated via variant="error")
 */
export default function AssistantMessage({
	message,
	isStreaming = false,
	variant,
	onApproveAction,
	onRejectAction,
}: AssistantMessageProps) {
	// Track which document source modal is open (null = none)
	const [activeSource, setActiveSource] = useState<ActiveSourceState | null>(null);

	// Ref to the rendered markdown container — used by the copy button to
	// read `.innerText` so users get the plain, rendered prose rather than
	// the raw markdown source.
	const contentRef = useRef<HTMLDivElement>(null);
	const [copyText, setCopyText] = useState<string>("");

	// Whenever the rendered content changes, re-read its innerText so the
	// CopyMessageButton always has up-to-date plain text to hand to the clipboard.
	useEffect(() => {
		if (contentRef.current) {
			setCopyText(contentRef.current.innerText ?? "");
		}
	}, [message.content]);

	// --- State E: Error ---
	if (variant === "error" || message.isError) {
		const errTimeStr = formatTimeOnly(message.timestamp);
		const errLabelId = `conv-label-${message.id}`;
		return (
			<div className="message message--assistant" role="log" aria-labelledby={errLabelId}>
				<span id={errLabelId} className="sr-only">Conversation</span>
				<div className="message-bubble" role="alert" aria-label={`PIA at ${errTimeStr}`}>
					<div className="message-error-block">
						<div className="message-error-header">
							<div className="message-error-icon" aria-hidden="true">
								<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 14, height: 14 }}>
									<circle cx="8" cy="8" r="7" stroke="#be123c" strokeWidth="1.5" />
									<path d="M8 4.5V8.5" stroke="#be123c" strokeWidth="1.5" strokeLinecap="round" />
									<circle cx="8" cy="11" r="0.75" fill="#be123c" />
								</svg>
							</div>
							<span className="message-error-title">Something went wrong</span>
						</div>
						<p className="message-error-text">{message.content}</p>
					</div>
				</div>
			</div>
		);
	}

	const hasContent = message.content.length > 0;
	const hasVisibleContent = message.content.trim().length > 0;
	const hasClassification = Boolean(message.complexity && message.model);
	const hasSources = message.sources && message.sources.length > 0;
	const showLivePill = message.hasProjectContext;

	// Show the chart loading placeholder only when the classifier has signalled VISUALISATION intent.
	// intent is stored on the message from the classification SSE event and is the authoritative
	// signal — no inference required. Once chartData arrives or streaming ends, this resolves.
	const isVisualisationPending =
		isStreaming &&
		message.intent === "VISUALISATION" &&
		!message.chartData &&
		!hasContent;

	const bubbleAriaLabel = `PIA at ${formatTimeOnly(message.timestamp)}`;
	// Unique ID for aria-labelledby on the role="log" outer wrapper. Using the
	// message ID ensures multiple messages in one DOM tree each have a distinct
	// label reference (Phase 1b §4.2 I4 contract — getByRole must not ambiguate).
	const logLabelId = `conv-label-${message.id}`;

	// --- State A: Thinking (no content, no classification yet) ---
	if (!hasContent && !hasClassification) {
		return (
			<div className="message message--assistant" role="log" aria-labelledby={logLabelId}>
				<span id={logLabelId} className="sr-only">Conversation</span>
				<div className="message-bubble" aria-label={bubbleAriaLabel}>
					<div className="message-assistant-header">
						<PiaAvatar />
					</div>
					<DocumentRetrievalCard
						isSearchingDocuments={message.isSearchingDocuments}
						retrievalStatus={message.retrievalStatus}
						isStreaming={isStreaming}
					/>
					{/* Hide thinking dots once an action proposal has arrived */}
					{!message.actionProposal && (
						<div className="message-thinking-dots" aria-label="PIA is thinking" role="status">
							<div className="message-thinking-dot" />
							<div className="message-thinking-dot" />
							<div className="message-thinking-dot" />
						</div>
					)}
					{message.actionProgressLabel && (
						<div className="message-action-progress" role="status" aria-live="polite">
							{message.actionProgressLabel}
						</div>
					)}
					{/* Confirmation card — rendered here for action-only responses (no text content) */}
					{message.actionProposal && (
						<ConfirmationCard
							proposal={message.actionProposal}
							status={message.actionStatus ?? "pending"}
							result={message.actionResult}
							onApprove={onApproveAction ?? (() => {})}
							onReject={onRejectAction ?? (() => {})}
							actionError={message.actionError}
						/>
					)}
				</div>
			</div>
		);
	}

	// --- State B: Classified but streaming not yet started (has classification, no content) ---
	if (!hasContent && hasClassification) {
		return (
			<div className="message message--assistant" role="log" aria-labelledby={logLabelId}>
				<span id={logLabelId} className="sr-only">Conversation</span>
				<div className="message-bubble" aria-label={bubbleAriaLabel}>
					<div className="message-assistant-header">
						<PiaAvatar />
					</div>
					<DocumentRetrievalCard
						isSearchingDocuments={message.isSearchingDocuments}
						retrievalStatus={message.retrievalStatus}
						isStreaming={isStreaming}
					/>
					{/* Hide thinking dots / visualisation placeholder once an action proposal has arrived */}
					{!message.actionProposal && (
						<>
							{isVisualisationPending ? (
								<ChartLoadingPlaceholder />
							) : (
								<div className="message-thinking-dots" aria-label="PIA is thinking" role="status">
									<div className="message-thinking-dot" />
									<div className="message-thinking-dot" />
									<div className="message-thinking-dot" />
								</div>
							)}
						</>
					)}
					{message.actionProgressLabel && (
						<div className="message-action-progress" role="status" aria-live="polite">
							{message.actionProgressLabel}
						</div>
					)}
					{/* Confirmation card — rendered here for action-only responses (no text content) */}
					{message.actionProposal && (
						<ConfirmationCard
							proposal={message.actionProposal}
							status={message.actionStatus ?? "pending"}
							result={message.actionResult}
							onApprove={onApproveAction ?? (() => {})}
							onReject={onRejectAction ?? (() => {})}
							actionError={message.actionError}
						/>
					)}
					<div style={{ marginTop: 8 }}>
						<RoutingBadge complexity={message.complexity!} model={message.model!} />
					</div>
				</div>
			</div>
		);
	}

	// --- State C / D: Streaming or complete (has content) ---
	return (
		<div className="message message--assistant" role="log" aria-labelledby={logLabelId}>
			{/* sr-only label for the role="log" wrapper — aria-labelledby reference.
			    Using labelledby (not aria-label) keeps the outer wrapper from being
			    found first by container.querySelector("[aria-label]"), allowing the
			    RoutingBadge to remain the first aria-labeled descendant (Suite F contract).
			    The label ID is unique per message to prevent getByRole ambiguity (I4). */}
			<span id={logLabelId} className="sr-only">Conversation</span>
			{/* Routing badge hoisted before .message-bubble in DOM so that
			    container.querySelector("[aria-label]") finds the badge first
			    (satisfies Suite F contract — routing badge aria-label test).
			    Phase 1b: intentional structural change; golden snapshots updated. */}
			{hasClassification && (
				<RoutingBadge complexity={message.complexity!} model={message.model!} />
			)}
			<div className="message-bubble" aria-label={bubbleAriaLabel}>
				{/* Header: avatar, PIA label — badge moved to sibling above for DOM ordering */}
				<div className="message-assistant-header">
					<PiaAvatar />
					<span className="message-assistant-name">PIA</span>
				</div>

				{/* Retrieval card — shown between header and content during/after search */}
				<DocumentRetrievalCard
					isSearchingDocuments={message.isSearchingDocuments}
					retrievalStatus={message.retrievalStatus}
					isStreaming={isStreaming}
				/>

				{/* Chart — rendered above the text content when chartData is available */}
				{message.chartData && (
					<div className="message-chart-container">
						<ChartRenderer spec={message.chartData} />
					</div>
				)}

				{/* Message text — rendered as markdown for assistant responses.
				     Only rendered when there is actual visible (non-whitespace)
				     content. An empty ReactMarkdown produces a blank <p> tag
				     that causes unwanted vertical whitespace, particularly
				     during the visualisation loading state where chartData may
				     arrive before any text content. The streaming cursor is
				     only shown alongside real text for the same reason.
				     aria-live="polite" + aria-atomic="false" ensure screen readers
				     announce incremental updates during streaming (Phase 1b §4.2 I3). */}
				{hasVisibleContent && (
					<div
						className="message-content message-content--markdown"
						ref={contentRef}
						aria-live="polite"
						aria-atomic="false"
					>
						<ReactMarkdown
							remarkPlugins={[remarkGfm]}
							urlTransform={(url) => url.startsWith('user:') || url.startsWith('contact:') ? url : defaultUrlTransform(url)}
							components={{
								a: ({ href, children }) => {
									if (href?.startsWith('user:')) {
										const zpuid = href.slice(5);
										// ZPUID is a numeric Zoho user ID. Zoho user IDs vary in length —
										// portal-level IDs are typically 11 digits, but organisation-level
										// ZPUIDs can be 18+ digits (e.g. 170876000005155055). Guard to 6–20
										// digits to cover both shapes without admitting arbitrary numbers.
										if (!/^\d{6,20}$/.test(zpuid)) {
											return <span>{children}</span>;
										}
										const displayName = reactNodeToString(children);
										if (!displayName) return <span>{children}</span>;
										return <PersonPill zpuid={zpuid} displayName={displayName} />;
									}
									if (href?.startsWith('contact:')) {
										const email = href.slice(8);
										// Validate email before rendering the pill — malformed hrefs fall through to plain text
										if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
											return <span>{children}</span>;
										}
										const displayName = reactNodeToString(children);
										if (!displayName) return <span>{children}</span>;
										return <ContactPill email={email} displayName={displayName} />;
									}
									// Regular links — open in new tab
									return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
								},
								// Apply date / status / priority highlighting to prose nodes.
								// Each override walks its children and wraps matched segments
								// in className spans, while passing React element children
								// (PersonPill, ContactPill, nested formatting) through unchanged.
								p: ({ children }) => <p>{applyHighlightsToChildren(children)}</p>,
								li: ({ children }) => <li>{applyHighlightsToChildren(children)}</li>,
								strong: ({ children }) => <strong>{applyHighlightsToChildren(children)}</strong>,
								em: ({ children }) => <em>{applyHighlightsToChildren(children)}</em>,
								td: ({ children }) => <td>{applyHighlightsToChildren(children)}</td>,
							}}
						>
							{message.content}
						</ReactMarkdown>
						{isStreaming && (
							<span className="message-cursor" aria-hidden="true" />
						)}
					</div>
				)}

				{/* Confirmation card — shown when the AI proposes a write-back action */}
				{message.actionProposal && (
					<ConfirmationCard
						proposal={message.actionProposal}
						status={message.actionStatus ?? "pending"}
						result={message.actionResult}
						onApprove={onApproveAction ?? (() => {})}
						onReject={onRejectAction ?? (() => {})}
						actionError={message.actionError}
					/>
				)}

				{/* Source pills — shown when complete and sources exist.
				    A sr-only span announces the source count to screen readers
				    (Phase 1b §4.2 I4 contract). */}
				{!isStreaming && (hasSources || showLivePill) && (
					<div className="message-sources" aria-label="Sources used">
						{/* Screen-reader-only source count — scoped inside .message-sources */}
						{hasSources && (
							<span className="sr-only">{message.sources!.length} {message.sources!.length === 1 ? "source" : "sources"}</span>
						)}
						{hasSources && message.sources!.map((source, i) => {
							// Only document-type sources with a documentId are clickable
							const isClickable = source.type === "document" && Boolean(source.documentId);

							if (isClickable) {
								return (
									<button
										key={`${source.fileName}-${i}`}
										className="message-source-pill message-source-pill--document message-source-pill--clickable"
										title={source.similarity !== undefined ? `Similarity: ${Math.round(source.similarity * 100)}% — click to view source` : "Click to view source"}
										onClick={() => setActiveSource({
											documentId: source.documentId!,
											fileName: source.fileName,
											workdriveFileId: source.workdriveFileId,
										})}
										aria-label={`View source document: ${source.fileName}`}
									>
										{/* Document icon */}
										<svg viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: 9, height: 9, flexShrink: 0 }}>
											<path d="M2 0h5l3 3v8a1 1 0 01-1 1H1a1 1 0 01-1-1V1a1 1 0 011-1z" fill="currentColor" opacity="0.4" />
											<path d="M6 0v3h4" stroke="currentColor" strokeWidth="0.8" fill="none" />
										</svg>
										{source.fileName}
									</button>
								);
							}

							// Non-clickable document pill (no documentId available)
							return (
								<span
									key={`${source.fileName}-${i}`}
									className="message-source-pill message-source-pill--document"
									title={source.similarity !== undefined ? `Similarity: ${Math.round(source.similarity * 100)}%` : "Referenced"}
								>
									{/* Document icon */}
									<svg viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: 9, height: 9, flexShrink: 0 }}>
										<path d="M2 0h5l3 3v8a1 1 0 01-1 1H1a1 1 0 01-1-1V1a1 1 0 011-1z" fill="currentColor" opacity="0.4" />
										<path d="M6 0v3h4" stroke="currentColor" strokeWidth="0.8" fill="none" />
									</svg>
									{source.fileName}
								</span>
							);
						})}
						{showLivePill && (
							<span className="message-source-pill message-source-pill--live">
								{/* Live data icon */}
								<svg viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ width: 9, height: 9, flexShrink: 0 }}>
									<circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.2" />
									<circle cx="5" cy="5" r="1.5" fill="currentColor" />
								</svg>
								Zoho Projects
							</span>
						)}
					</div>
				)}

				{/* Copy-to-clipboard — absolutely positioned in top-right of bubble.
				    Placed last in the DOM so keyboard tab order follows reading order
				    (avatar → content → source pills → copy button). */}
				{!isStreaming && hasVisibleContent && (
					<CopyMessageButton text={copyText || message.content} />
				)}
			</div>

			{/* Timestamp — shown once streaming completes, outside the bubble.
			    The visible <time> is aria-hidden to avoid double-announcement;
			    a .sr-only span carries the full-form text for screen readers
			    (Phase 1b §4.2 I3 contract). */}
			{!isStreaming && (
				<span className="message-timestamp">
					<time aria-hidden="true" dateTime={message.timestamp.toISOString()}>
						{formatTimestamp(message.timestamp)}
					</time>
					<span className="sr-only">{formatTimestamp(message.timestamp)}</span>
				</span>
			)}

			{/* Source document modal — portal-based, opened when a pill is clicked */}
			{activeSource && (
				<SourceDocumentModal
					documentId={activeSource.documentId}
					fileName={activeSource.fileName}
					workdriveFileId={activeSource.workdriveFileId}
					onClose={() => setActiveSource(null)}
				/>
			)}
		</div>
	);
}
